/** Validated, read-only staff calculator boundary over the shared SAH engines. */
import {
  SAH_BUDGET_SCHEDULES,
  getSahBudgetForClient,
  sahBudgetScheduleFor,
  sahContributionCapsFor,
  type SahBudgetResolution,
} from './award-data/sah';
import {
  buildSahBudgetScenario,
  type SahBudgetScenarioLineInput,
  type SahBudgetScenarioResult,
} from './sah-budget-scenarios';
import {
  estimateSahFees,
  projectLineContribution,
  type SahActiveHardship,
  sahHardshipForDate,
} from './sah-fee-estimate';
import type { SahMeansTier } from './sah';
import { DEFAULT_TZ, getMonthInTz, getYearInTz } from './timezone';

export const SAH_CALCULATOR_VERSION = 's2-v2';

/** Resolve an instant's quarter using the tenant's calendar, including UTC cutovers. */
export function sahPlanningQuarterStart(
  now: Date = new Date(),
  timezone: string = DEFAULT_TZ,
): string {
  return `${getYearInTz(now, timezone)}-${String(Math.floor(getMonthInTz(now, timezone) / 3) * 3 + 1).padStart(2, '0')}-01`;
}

export function isSahCalculatorDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day!));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month && date.getUTCDate() === day
  );
}

function quarterEnd(start: string): string {
  const year = Number(start.slice(0, 4));
  const month = Number(start.slice(5, 7));
  const end = new Date(Date.UTC(year, month + 2, 0));
  return `${year}-${String(month + 2).padStart(2, '0')}-${String(end.getUTCDate()).padStart(2, '0')}`;
}

// Do not project the open-ended latest schedule into unverified future quarters.
export const SAH_CALCULATOR_SUPPORTED_FROM = SAH_BUDGET_SCHEDULES[0]!.effectiveFrom;
const latestFundingDate = SAH_BUDGET_SCHEDULES[SAH_BUDGET_SCHEDULES.length - 1]!.effectiveFrom;
export const SAH_CALCULATOR_SUPPORTED_TO = quarterEnd(
  `${latestFundingDate.slice(0, 4)}-${String(Math.floor((Number(latestFundingDate.slice(5, 7)) - 1) / 3) * 3 + 1).padStart(2, '0')}-01`,
);

export type SahCalculatorLine = Omit<
  SahBudgetScenarioLineInput,
  'bucket' | 'unitCost' | 'quantity' | 'lifetimeCapReached'
> & {
  unitCost: number;
  quantity: number;
  /** Staff v1 ongoing funding pools only. Pathway/private pools are out of scope. */
  bucket?: 'DIRECT_CARE' | 'CARE_MANAGEMENT';
};

export type SahBudgetCalculatorInput = {
  quarterStart: string;
  /** Contribution examples use this date; defaults to the selected quarter start. */
  plannedDate?: string;
  fundingBasis: 'SAH' | 'HCP';
  classificationLevel?: number | null;
  transitionedHcpLevel?: number | null;
  meansTier?: SahMeansTier | null;
  /** Independent of funding basis. Never inferred from transitioned HCP funding. */
  isGrandfathered?: boolean;
  assessedIndependence?: number | null;
  assessedEverydayLiving?: number | null;
  activeHardship?: SahActiveHardship | null;
  lines?: readonly SahCalculatorLine[];
};

const tiers = new Set(['FULL_PENSIONER', 'PART_PENSIONER', 'CSHC_HOLDER', 'SELF_FUNDED_NO_CSHC']);
const categories = new Set(['CLINICAL', 'INDEPENDENCE', 'EVERYDAY_LIVING']);
const validLevel = (level: number | null | undefined, max: number) =>
  level == null || (Number.isInteger(level) && level >= 1 && level <= max);
const validRate = (rate: number | null | undefined) =>
  rate == null || (Number.isFinite(rate) && rate >= 0 && rate <= 1);

/** Runs before any permissive date selector or scenario engine is called. */
export function validateSahBudgetCalculatorInput(input: SahBudgetCalculatorInput): string[] {
  const errors: string[] = [];
  if (
    !isSahCalculatorDate(input.quarterStart) ||
    !/^\d{4}-(01|04|07|10)-01$/.test(input.quarterStart)
  ) {
    errors.push('Choose a valid planning quarter beginning on 1 January, April, July or October.');
  } else if (
    input.quarterStart < SAH_CALCULATOR_SUPPORTED_FROM ||
    quarterEnd(input.quarterStart) > SAH_CALCULATOR_SUPPORTED_TO
  ) {
    errors.push(
      `Planning quarter unsupported. Complete quarters are supported from January 2026 through ${SAH_CALCULATOR_SUPPORTED_TO}.`,
    );
  }
  const plannedDate = input.plannedDate ?? input.quarterStart;
  if (
    !isSahCalculatorDate(plannedDate) ||
    plannedDate < input.quarterStart ||
    plannedDate > quarterEnd(input.quarterStart)
  ) {
    errors.push('Choose a valid planned date within the selected quarter.');
  }
  if (input.fundingBasis !== 'SAH' && input.fundingBasis !== 'HCP')
    errors.push('Choose a supported funding basis.');
  if (!validLevel(input.classificationLevel, 8) || !validLevel(input.transitionedHcpLevel, 4))
    errors.push('Choose a valid classification or transitioned HCP level.');
  if (
    (input.fundingBasis === 'SAH' && input.transitionedHcpLevel != null) ||
    (input.fundingBasis === 'HCP' && input.classificationLevel != null)
  )
    errors.push('Choose one funding basis and its matching level.');
  if (input.isGrandfathered != null && typeof input.isGrandfathered !== 'boolean')
    errors.push('Choose standard or no-worse-off contribution treatment.');
  if (input.meansTier != null && !tiers.has(input.meansTier))
    errors.push('Choose a supported contribution basis or leave it unknown.');
  if (!validRate(input.assessedIndependence) || !validRate(input.assessedEverydayLiving))
    errors.push('Assessed contribution rates must be finite fractions from 0 to 1.');
  const validateHardship = (hardship: SahActiveHardship | null | undefined) => {
    if (!hardship) return;
    if (hardship.reductionKind !== 'FULL_WAIVER' && hardship.reductionKind !== 'PARTIAL')
      errors.push('Choose a valid hardship determination.');
    if (
      hardship.reductionKind === 'PARTIAL' &&
      (hardship.reducedContributionPercent == null ||
        !Number.isFinite(hardship.reducedContributionPercent) ||
        hardship.reducedContributionPercent < 0 ||
        hardship.reducedContributionPercent > 100)
    )
      errors.push('A partial hardship determination requires a valid assessed percentage.');
    if (
      (hardship.effectiveFrom != null && !isSahCalculatorDate(hardship.effectiveFrom)) ||
      (hardship.effectiveTo != null && !isSahCalculatorDate(hardship.effectiveTo)) ||
      (hardship.effectiveFrom &&
        hardship.effectiveTo &&
        hardship.effectiveFrom > hardship.effectiveTo)
    )
      errors.push('Choose valid inclusive hardship start and end dates in date order.');
  };
  validateHardship(input.activeHardship);
  for (const line of input.lines ?? []) {
    validateHardship(line.activeHardship);
    if (
      line.storedContributionPercent != null &&
      (!Number.isFinite(line.storedContributionPercent) ||
        line.storedContributionPercent < 0 ||
        line.storedContributionPercent > 100)
    )
      errors.push('Every stored contribution percentage must be finite from 0 to 100.');
    if (line.lifetimeCapReachedAt != null && !isSahCalculatorDate(line.lifetimeCapReachedAt))
      errors.push('Choose a valid lifetime-cap notice date.');
    const date = line.serviceDate ?? plannedDate;
    if (
      !isSahCalculatorDate(date) ||
      date < input.quarterStart ||
      date > quarterEnd(input.quarterStart)
    )
      errors.push('Every planned service date must be within the selected quarter.');
    if (
      !Number.isFinite(line.unitCost) ||
      line.unitCost < 0 ||
      !Number.isFinite(line.quantity) ||
      line.quantity <= 0
    )
      errors.push(
        'Every service line requires a non-negative price and positive quarter quantity.',
      );
    if (line.bucket != null && line.bucket !== 'DIRECT_CARE' && line.bucket !== 'CARE_MANAGEMENT')
      errors.push('Choose an ongoing direct-care or care-management service.');
    if (!line.serviceCategory || !categories.has(line.serviceCategory))
      errors.push('Every service line requires a known contribution category.');
    if (!validRate(line.individualAssessedRate))
      errors.push('Every assessed line rate must be a finite fraction from 0 to 1.');
  }
  return errors;
}

export function calculateSahBudgetCalculator(input: SahBudgetCalculatorInput) {
  const errors = validateSahBudgetCalculatorInput(input);
  if (errors.length) return { status: 'invalid' as const, errors };
  const plannedDate = input.plannedDate ?? input.quarterStart;
  const fundingSchedule = sahBudgetScheduleFor(input.quarterStart);
  const caps = sahContributionCapsFor(plannedDate);
  const budget: SahBudgetResolution = getSahBudgetForClient({
    classificationLevel: input.classificationLevel,
    transitionedHcpLevel: input.transitionedHcpLevel,
    asOfDate: input.quarterStart,
  });
  const fees = estimateSahFees({
    tier: input.meansTier ?? null,
    isGrandfathered: input.isGrandfathered ?? false,
    assessedIndependence: input.assessedIndependence,
    assessedEverydayLiving: input.assessedEverydayLiving,
    activeHardship: sahHardshipForDate(input.activeHardship, plannedDate),
    asOfDate: plannedDate,
  });
  const scenario: SahBudgetScenarioResult | null =
    budget.source === 'unclassified'
      ? null
      : buildSahBudgetScenario({
          mode: input.fundingBasis === 'HCP' ? 'ONGOING_TRANSITIONED_HCP' : 'ONGOING_STANDARD',
          classificationLevel: input.classificationLevel,
          transitionedHcpLevel: input.transitionedHcpLevel,
          meansTier: input.meansTier,
          activeHardship: input.activeHardship,
          isGrandfathered: input.isGrandfathered,
          asOfDate: input.quarterStart,
          assessedRates: {
            ...(input.assessedIndependence == null
              ? {}
              : { INDEPENDENCE: input.assessedIndependence }),
            ...(input.assessedEverydayLiving == null
              ? {}
              : { EVERYDAY_LIVING: input.assessedEverydayLiving }),
          },
          lines: (input.lines ?? []).map((line) => ({
            ...line,
            serviceDate: line.serviceDate ?? plannedDate,
          })),
        });
  const personalCare = projectLineContribution({
    serviceCategory: 'INDEPENDENCE',
    serviceType: 'personal_care',
    deliveryDate: plannedDate,
    billableAmount: 100,
    means: input.meansTier
      ? {
          tier: input.meansTier,
          isGrandfathered: input.isGrandfathered ?? false,
          assessedIndependence: input.assessedIndependence,
        }
      : null,
    activeHardship: input.activeHardship,
  });
  return {
    status: 'ready' as const,
    errors: [],
    budget,
    fundingState: budget.source === 'unclassified' ? ('unknown' as const) : ('known' as const),
    contributionState:
      fees.independence.needsAssessedRate || fees.everydayLiving.needsAssessedRate
        ? ('assessment_required' as const)
        : ('known' as const),
    fees,
    personalCare,
    scenario,
    provenance: {
      engineVersion: SAH_CALCULATOR_VERSION,
      quarterStart: input.quarterStart,
      quarterEnd: quarterEnd(input.quarterStart),
      plannedDate,
      fundingEffectiveFrom: fundingSchedule.effectiveFrom,
      fundingEffectiveTo: fundingSchedule.effectiveTo,
      fundingSource: fundingSchedule.label,
      contributionCapsEffectiveFrom: caps.effectiveFrom,
      lifetimeCap: input.isGrandfathered
        ? caps.lifetimeCapGrandfathered
        : caps.lifetimeCapNewEntrant,
      lineContributionCapDates: (input.lines ?? []).map(
        (line) => sahContributionCapsFor(line.serviceDate ?? plannedDate).effectiveFrom,
      ),
      personalCareClinicalFrom: '2026-10-01',
      sourceBasis:
        'Checked-in SAH funding schedules and contribution-cap periods in award-data/sah.ts; not a new regulatory verification.',
    },
  };
}

/** Display-only affordable whole units; a free service has no price-derived limit. */
export function sahMaximumAffordableUnits(
  budget: number | null,
  price: number | null,
): number | null {
  if (
    budget == null ||
    price == null ||
    !Number.isFinite(budget) ||
    !Number.isFinite(price) ||
    budget < 0 ||
    price <= 0
  )
    return null;
  // eslint-disable-next-line statura/no-floating-point-money -- converts dollar inputs to integer cents before any arithmetic.
  const budgetCents = Math.round(budget * 100);
  // eslint-disable-next-line statura/no-floating-point-money -- converts dollar inputs to integer cents before any arithmetic.
  const priceCents = Math.round(price * 100);
  if (priceCents === 0) return null;
  return Math.floor(budgetCents / priceCents);
}
